import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import Select from '@material-ui/core/Select';
import { Formik } from 'formik';

import { toast } from 'react-toastify';
import CircularProgress from '@material-ui/core/CircularProgress';
import { CONSULTATION_CHOICES } from '../../../shared/constants/constantChoices';
import { personalConsultationFormValidationSchema } from '../../../shared/validators/home.schemas';
import TextInput from '../../Shared/layouts/TextInput';
import { InputChangeState } from '../../../globals/models/components/textInput';
import { submitHomeContactForm } from '../../../shared/helpers/home';

const PersonalConsultationFormInitialValues = {
  phone: '',
  name: '',
  timeslot: '',
};

const PersonalConsultationForm = ({
  canSubmit,
}: PersonalConsultationFormProps) => {
  const { t } = useTranslation();
  const [isSubmitting, setIsSubmitting] = useState(false);

  return (
    <Formik
      onSubmit={(values, formikHelpers) => {
        setIsSubmitting(true);
        submitHomeContactForm(values)
          .then(() => {
            toast.success('CONTACT_FORM.SUBMISSION_SUCCESS');
            setIsSubmitting(false);
            formikHelpers.resetForm();
          })
          .catch(() => {
            setIsSubmitting(false);
            toast.error(t('CONTACT_FORM.SUBMISSION_ERROR'));
          });
      }}
      validateOnChange
      initialValues={PersonalConsultationFormInitialValues}
      validationSchema={personalConsultationFormValidationSchema}
    >
      {(props) => {
        const {
          values,
          touched,
          errors,
          handleBlur,
          handleChange,
          isValid,
          handleSubmit,
        } = props;

        const isFormTouched = Object.values(touched).length > 0;
        const isFormValid =
          isValid &&
          Object.values(errors).length === 0 &&
          isFormTouched &&
          canSubmit;

        return (
          <div className="pb-16 md:pl-20 pl-6 md:pr-0 pr-6">
            <h1 className="font-bold md:text-2xl text-xl pb-8">
              {t('HOME_PAGE_CONTACT_FORM_TITLE_2')}
            </h1>
            <p className="text-lg">{t('HOME_CONTACT_FORM_EXPL_TEXT_2')}</p>
            <div className="py-8">
              <div className="grid grid-cols-2 gap-10">
                <TextInput
                  forText="contactFormName1"
                  isRequired
                  labelKey={t('NAME_TEXT')}
                  onChange={(value, event) => {
                    handleChange(event);
                  }}
                  onBlur={handleBlur}
                  changeState={
                    touched.name && errors.name
                      ? InputChangeState.INVALID
                      : InputChangeState.VALID
                  }
                  errorMessage={errors.name}
                  value={values.name}
                  classes={{
                    input: 'border border-gray-500 rounded-sm w-full h-8 mt-2',
                  }}
                  name="name"
                  disabled={isSubmitting}
                />
                <TextInput
                  forText="contactFormPhoneNumber"
                  isRequired
                  labelKey={t('PHONE_FIELD_TEXT')}
                  onChange={(value, event) => {
                    handleChange(event);
                  }}
                  onBlur={handleBlur}
                  changeState={
                    touched.phone && errors.phone
                      ? InputChangeState.INVALID
                      : InputChangeState.VALID
                  }
                  errorMessage={errors.phone}
                  value={values.phone}
                  classes={{
                    input: 'border border-gray-500 rounded-sm w-full h-8 mt-2',
                  }}
                  name="phone"
                  disabled={isSubmitting}
                />
              </div>
              <div className="grid grid-cols-2 gap-10 pt-6">
                <div>
                  <label htmlFor="time">{t('TIME_TEXT')}</label>
                  <br />
                  <Select
                    name="timeslot"
                    id="time-choices"
                    className="border border-gray-500 rounded-sm w-full h-8 mt-2 px-1"
                    disableUnderline
                    onBlur={handleBlur}
                    onChange={handleChange}
                    disabled={isSubmitting}
                    value={values.timeslot}
                    native
                  >
                    {CONSULTATION_CHOICES.map((el) => (
                      <option
                        value={el.value}
                        key={el.translationKey}
                        className="hover:bg-grey-4 cursor-pointer"
                      >
                        {t(el.translationKey)}
                      </option>
                    ))}
                  </Select>
                  {touched.timeslot && errors.timeslot && (
                    <p className="text-red-400 p-0 text-sm">
                      {errors.timeslot}
                    </p>
                  )}
                </div>
              </div>
            </div>
            <p className="card-footer">
              <button
                type="button"
                className={`button focus:outline-none ${
                  isFormValid
                    ? 'cursor-pointer bg-primary'
                    : 'cursor-not-allowed bg-primary-disabled'
                }`}
                disabled={!isFormValid || isSubmitting}
                onClick={() => handleSubmit()}
              >
                {t('HOME_CONTACT_FORM_SUBMIT_TEXT_2')}
                {isSubmitting && (
                  <CircularProgress
                    style={{ color: 'white' }}
                    size={15}
                    className="ml-2"
                  />
                )}
              </button>
            </p>
          </div>
        );
      }}
    </Formik>
  );
};

interface PersonalConsultationFormProps {
  canSubmit: boolean;
}

export default PersonalConsultationForm;
